import React, { Fragment, ChangeEvent, useEffect } from 'react';
import { FieldProps, FieldArray } from 'formik';
import { NetworkStatus, useQuery } from '@apollo/client';
import { get } from 'lodash';
import { Spinner, SpinnerSize, Text, Checkbox, Box } from '@bechtel/gds';
import { SubHeader, LabelStyled } from '../styled';
import { CommodityType } from '@bechtel/types';
import { useAppUser, ErrorRetry, useProjectCommodityTypesQuery } from '@bechtel/client-common';
import { Trans } from '@lingui/macro';

export const CommodityTypeList: React.FC<FieldProps> = ({ form }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useProjectCommodityTypesQuery(user?.project?.id);
  const commodityTypeList: CommodityType[] = get(data, 'projectConfigById.projectCommodityTypes', []);

  useEffect(() => {
    form.setFieldValue('commodityTypesCount', commodityTypeList.length);
  }, [data]);

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Commodity Type List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <FieldArray name="commodityTypes">
            {({ remove, push }) => {
              const onChangeFn = (e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (form.values.commodityTypes.includes(value)) {
                  const index = form.values.commodityTypes.findIndex((entity: string) => {
                    return entity === value;
                  });
                  remove(index);
                } else {
                  push(value);
                }
              };

              return (
                <Fragment>
                  <SubHeader>
                    <Box>
                      <Box mr={{ sm: '600' }}>
                        <Checkbox
                          type="checkbox"
                          checked={form.values.commodityTypes.length === commodityTypeList.length}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            e.target.checked
                              ? form.setFieldValue(
                                  'commodityTypes',
                                  commodityTypeList.map((entity) => {
                                    return entity.name;
                                  })
                                )
                              : form.setFieldValue('commodityTypes', [])
                          }
                        />
                      </Box>
                      <Text color="grey.1000" size="400" weight="600">
                        <Trans>Commodity</Trans>
                      </Text>
                    </Box>
                    {form.values.commodityTypes.length > 0 && (
                      <Text color="grey.1000" size="400">
                        {form.values.commodityTypes.length} <Trans>Selected</Trans>
                      </Text>
                    )}
                  </SubHeader>
                  {commodityTypeList.map(({ name, id }, index) => {
                    return (
                      <LabelStyled key={index} data-testid={`backlog-filter-commodity-types-${id}`}>
                        <Box mr={{ sm: '600' }}>
                          <Checkbox
                            value={name}
                            type="checkbox"
                            onChange={onChangeFn}
                            checked={form.values.commodityTypes.includes(name)}
                          />
                        </Box>
                        <Text color="grey.1000" size="400">
                          {name}
                        </Text>
                      </LabelStyled>
                    );
                  })}
                </Fragment>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
